import * as React from "react";
import {
  TableCell,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Snackbar,
} from "@mui/material";
import RestUtilities, { IRestResponse } from "../../../services/RestUtilities";
import Users from "../../../models/Users";
import UserRolesModel, { UserRoles } from "../../../models/UserRoles";
import EditIcon from "@mui/icons-material/Edit";
import EditUserModal from "../../../components/administration/UserData/EditUserModal";
import Auth from "../../../services/Auth";
import AuthStore from "../../../stores/Auth";
import SnackbarWrapper from "../../../components/SnackbarWrapper";
import Loader from "../../../components/Loader";
import UserGroups from "../../../models/UserGroups";
import { Temporal } from "@js-temporal/polyfill";
import { GrowMessage } from "../../../components/GrowMessage";

class FilterUsersModel {
  personalNr: string = "";
  firstname: string = "";
  lastname: string = "";
  active: boolean;
  userRoleId: number;
  [key: string]: any;
}

class UserData extends React.Component<any, any> {
  public static TableColumns: string[] = [
    "ID",
    "Personalnr.",
    "Vorname",
    "Nachname",
    "Rolle",
    "Benutzergruppe",
    "Inaktiv seit",
    "Aktiv",
    "Registriert",
    "Datenschutz",
    "Aktionen",
  ];

  state = {
    users: [] as Users[],
    userRoles: [] as UserRolesModel[],
    page: 0,
    rowsPerPage: 20,
    filter: new FilterUsersModel(),
    userToEdit: new Users(),
    showEditModal: false,
    showError: false,
    errorMessage: "",
    loading: false,
    userGroups: [] as UserGroups[],
  };

  handleChangePage = (event: any, page: number) => {
    this.setState({ page });
  };

  handleHideModal = () => {
    this.setState({ showEditModal: false });
  };

  handleFilterChange = (event: any): void => {
    const newFilter = { ...this.state.filter };
    newFilter[event.target.name] = event.target.value;

    this.setState({ filter: newFilter });
  };

  clearFilters = () => {
    const newFilter = new FilterUsersModel();

    this.setState({ filter: newFilter });
    this.fetchUsers();
  };

  handleSearch = () => {
    this.fetchUsers(this.state.filter);
  };

  handleEditChange = (event: any) => {
    const newModel = { ...this.state.userToEdit } as Users;
    let value = event.target.value;

    //allow only digits in Vereinsnummer
    if (event.target.name === "clubNumber") {
      value = value.replace(/[^\d]+/g, "");
    }

    newModel[event.target.name] = value;
    this.setState({ userToEdit: newModel });
  };

  editUser = () => {
    RestUtilities.put<Users>("/api/Users/Edit", this.state.userToEdit)
      .then((resp) => {
        this.setState({ showEditModal: false });
        this.fetchUsers();
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  fetchUsers = (filter: FilterUsersModel = new FilterUsersModel()) => {
    this.setState({ loading: true });

    const { personalNr, firstname, lastname, userRoleId, active } = filter;

    let url = "/api/Users?";

    if (personalNr !== "") url += `personalNr=${personalNr}&`;

    if (firstname !== "") url += `firstname=${firstname}&`;

    if (lastname !== "") url += `lastname=${lastname}&`;

    if (typeof userRoleId !== "undefined") url += `userRoleId=${userRoleId}&`;

    if (typeof active !== "undefined") url += `active=${active}&`;

    RestUtilities.get<Users[]>(url)
      .then((resp) => {
        let users = resp.content as Users[];
        this.setState({ users: users, loading: false });
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  fetchUserRoles = () => {
    RestUtilities.get<UserRolesModel[]>("/api/UserRoles")
      .then((resp) => {
        let userRoles = resp.content as UserRolesModel[];
        this.setState({ userRoles: userRoles });
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  fetchUserGroups = () => {
    RestUtilities.get<UserGroups[]>("/api/UserGroups")
      .then((resp) => {
        let userGroups = resp.content as UserGroups[];
        this.setState({ userGroups: userGroups });
      })
      .catch((resp) => {
        this.showError(resp);
      });
  };

  importUsers = (): void => {
    let file = (document.getElementById("importFile") as any).files[0];
    if(!file) {
        return;
    }
    let fileReader = new FileReader();
    fileReader.onloadend = () => {
      this.setState({ loading: true });
      let bytes = btoa(fileReader.result as string);
      RestUtilities.post("/api/Users/Import", bytes)
        .then((resp) => {
          this.fetchUsers();
        })
        .catch((resp) => {
          this.showError(resp);
        });
    };
    fileReader.readAsBinaryString(file);
  };

  exportUsers = (): void => {
    let xhr = new XMLHttpRequest();
    let self = this;
    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200) {
          if (xhr.response.size < 200) {
            let reader = new FileReader();
            reader.onload = () => {
              let result = JSON.parse(reader.result as string);
            };
            reader.readAsText(this.response);

            self.setState({ loading: false });
            return false;
          }
          let fileName: string = "";
          let disposition = xhr.getResponseHeader("Content-Disposition");

          if (disposition != null) {
            let startIndex = disposition!.indexOf("filename=") + 9;
            fileName = disposition!.substring(21, disposition!.length);
          }

          let blob = new Blob([this.response]);

          let reader = new FileReader();
          reader.onload = () => {
            let result = {} as any;
            try {
              result = JSON.parse(reader.result as string);
            } catch (ex) {
                RestUtilities.saveAs(blob, fileName);
            }

            if (result.error != null) {
              self.setState({
                loading: false,
                showMessage: true,
                message: result.error,
              });
              return;
            }
          };

          reader.readAsText(blob);

          self.setState({ loading: false });
        }
      }
    };

    xhr.open("GET", "/api/Reports/ExportUsers");
    xhr.setRequestHeader("Content-type", "application/json");
    xhr.setRequestHeader('Authorization',`Bearer ${AuthStore.getToken()}`);
    xhr.responseType = "arraybuffer";
    xhr.send();
  };

  componentDidMount() {
    this.fetchUsers();
    this.fetchUserRoles();
    this.fetchUserGroups();
  }

  mapRowCells = (user: Users) => {
    return (
      <TableRow key={user.id}>
        <TableCell>{user.id}</TableCell>
        <TableCell>{user.personalNr}</TableCell>
        <TableCell>{user.firstname}</TableCell>
        <TableCell>{user.lastname}</TableCell>
        <TableCell>{UserRoles[user.userRoleId]}</TableCell>
        <TableCell>{user.userGroupName}</TableCell>
        <TableCell>
          {user.deactivationDate
            ? Temporal.Instant.from( new Date(user.deactivationDate).toISOString())
                .toZonedDateTimeISO("UTC")
                .toPlainDate()
                .toString()
            : ""}
        </TableCell>
        <TableCell>{user.active === true ? "ja" : "nein"}</TableCell>
        <TableCell>{user.registered === true ? "ja" : "nein"}</TableCell>
        <TableCell>{user.acceptedTerms === true ? "ja" : "nein"}</TableCell>
        <TableCell>
          <EditIcon
            className="icon"
            onClick={() => {
              this.setState({ userToEdit: user, showEditModal: true });
            }}
          />
        </TableCell>
      </TableRow>
    );
  };

  handleSnackbarClose = (event: any, reason: string) => {
    if (reason === "clickaway") return;

    this.setState({ showError: false });
  };

  showError = (resp: IRestResponse<Users>): void => {
    if (typeof resp.error_content != "undefined" && resp.error_content) {
      this.setState({
        showError: true,
        errorMessage: resp.error_content,
        loading: false,
      });
    }
  };

  public render() {
    let { page, rowsPerPage } = this.state;

    let columns: JSX.Element[] = UserData.TableColumns.map((x) => {
      return <TableCell key={x}>{x}</TableCell>;
    });

    let tableRows = [];
    tableRows = this.state.users
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((x) => {
        return this.mapRowCells(x);
      });

    const userRoles: JSX.Element[] = (
      this.state.userRoles as UserRolesModel[]
    ).map((x) => {
      return (
        <MenuItem key={x.id} value={x.id}>
          {x.description}
        </MenuItem>
      );
    });

    const userGroups: JSX.Element[] = (
      this.state.userGroups as UserGroups[]
    ).map((x) => {
      return (
        <MenuItem key={x.id} value={x.id}>
          {x.groupName}
        </MenuItem>
      );
    });

    return (
      <>
        <h2 className="mt-3 title">Stammdaten - Benutzerdaten</h2>
        <div className="card">
          <div className="card-header">
            <h5>Filters</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-sm-5">
                <TextField
                  label="Personalnummer:"
                  name="personalNr"
                  value={
                    typeof this.state.filter.personalNr !== "undefined"
                      ? this.state.filter.personalNr
                      : ""
                  }
                  onChange={(e: any) => this.handleFilterChange(e)}
                  fullWidth
                ></TextField>
              </div>
              <div className="col-sm-5 offset-sm-1">
                <TextField
                  label="Vorname:"
                  name="firstname"
                  value={
                    typeof this.state.filter.firstname !== "undefined"
                      ? this.state.filter.firstname
                      : ""
                  }
                  onChange={(e: any) => this.handleFilterChange(e)}
                  fullWidth
                ></TextField>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-sm-5">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel htmlFor="outlined-rolle">Rolle</InputLabel>
                  <Select
                    input={<OutlinedInput id="outlined-rolle" fullWidth />}
                    fullWidth
                    name="userRoleId"
                    value={
                      typeof this.state.filter.userRoleId !== "undefined"
                        ? this.state.filter.userRoleId
                        : ""
                    }
                    onChange={(e: any) => this.handleFilterChange(e)}
                  >
                    <MenuItem value="">
                      <em></em>
                    </MenuItem>
                    {userRoles}
                  </Select>
                </FormControl>
              </div>
              <div className="col-sm-5 offset-sm-1">
                <TextField
                  label="Nachname:"
                  name="lastname"
                  value={
                    typeof this.state.filter.lastname !== "undefined"
                      ? this.state.filter.lastname
                      : ""
                  }
                  onChange={(e: any) => this.handleFilterChange(e)}
                  fullWidth
                ></TextField>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-sm-5">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel htmlFor="outlined-aktiv">Aktiv</InputLabel>
                  <Select
                    input={<OutlinedInput id="outlined-aktiv" fullWidth />}
                    fullWidth
                    name="active"
                    value={
                      typeof this.state.filter.active !== "undefined"
                        ? this.state.filter.active
                        : ""
                    }
                    onChange={(e: any) => this.handleFilterChange(e)}
                  >
                    <MenuItem value="">
                      <em></em>
                    </MenuItem>
                    <MenuItem value={"true"}>ja</MenuItem>
                    <MenuItem value={"false"}>nein</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="float-right mt-4">
              <Button
                variant="contained"
                color="primary"
                className="btnBlue"
                onClick={() => this.handleSearch()}
              >
                Suchen
              </Button>
              <Button
                className="ml-2 btnBlue"
                variant="contained"
                color="primary"
                onClick={() => this.clearFilters()}
              >
                Zurücksetzen
              </Button>
            </div>
          </div>
        </div>
        {AuthStore.getRole() === UserRoles[UserRoles.Administrator] &&
          Auth.isSignedIn() && (
            <div className="row">
              <input type="file" id="importFile" className="col-sm-3 mt-3" />
              <Button
                className="col-sm-3 mt-3 mr-2 btnBlue"
                color="primary"
                variant="contained"
                onClick={() => this.importUsers()}
              >
                Importieren
              </Button>
              <Button
                className="col-sm-3 ml-2 mt-3 mr-2 btnBlue"
                color="primary"
                variant="contained"
                onClick={() => this.exportUsers()}
              >
                Excel Export
              </Button>
            </div>
          )}
        <div className="row mt-3 overflowTable">
          <Table className="objects-table table">
            <TableHead>
              <TableRow>{columns}</TableRow>
            </TableHead>
            <TableBody>{tableRows}</TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={this.state.users.length}
            rowsPerPage={20}
            page={this.state.page}
            backIconButtonProps={{
              "aria-label": "Previous Page",
            }}
            nextIconButtonProps={{
              "aria-label": "Next Page",
            }}
            onPageChange={this.handleChangePage}
          />
        </div>
        {this.state.showEditModal && (
          <EditUserModal
            user={this.state.userToEdit}
            hide={this.handleHideModal}
            submit={this.editUser}
            changeNewObject={this.handleEditChange}
            userRoles={userRoles}
            userGroups={userGroups}
          />
        )}
        <GrowMessage
              anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
              }}
              message={this.state.errorMessage}
              visible={this.state.showError}
              autoHideDuration={6000}
              onClose={this.handleSnackbarClose} ></GrowMessage>  
        {/* <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          open={this.state.showError}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
        >
          <SnackbarWrapper
            onClose={this.handleSnackbarClose}
            variant="success"
            message={this.state.errorMessage}
          />
        </Snackbar> */}
        {this.state.loading && <Loader />}
      </>
    );
  }
}

export default UserData;
